/* eslint-disable radix */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from 'Context';
import { dataFetcher, endpoints, STRIPE_PUBLIC_KEY } from 'Api';
import { Pathname } from 'Routes';
import { isFreePlan, isLifetimePlan } from 'Utils/paymentGate';
import logo from 'Assets/Login Logo.png';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import styles from './QuickUserPayment.module.css';

// Default feature points shown while the plan API is still loading / if the
// current plan carries no description (mirrors the free "Deliver & Stream" plan).
const DEFAULT_FEATURES = ['Unlimited Storage', 'Unlimited Uploads', 'Download Media', 'Limited Streaming'];

export const QuickUserPayment = () => {
  const navigate = useNavigate();
  const { setAppSnackbar, getSubscription, subscriptionDetails, allPlans } = useContext(AppContext);

  // Make sure we have the latest subscription (the quick user's current plan).
  useEffect(() => {
    getSubscription?.();
  }, []);

  // Lifetime accounts have nothing left to bill, so this page has no job to do
  // for them. Avatar.jsx already routes them past it — this is the backstop for
  // a direct hit, or for the case where the subscription hadn't loaded yet when
  // that routing decision was made.
  useEffect(() => {
    if (isLifetimePlan(subscriptionDetails, allPlans)) {
      navigate(Pathname.profile.basePath, { replace: true });
    }
  }, [subscriptionDetails, allPlans]);

  // ── Which plan does the card show? ──
  // Free-plan accounts are never locked here — the only ones who see this page
  // on the free plan are quick users still in onboarding, and for them the
  // ANNUAL plan is the OFFER being made, not their placeholder "Deliver &
  // Stream" plan. Everyone else arrives because the no-card lock sent them,
  // and they must see the plan they ACTUALLY hold (Premium Monthly, etc.).
  //
  // Deciding it off the plan itself means no lane flag has to be threaded
  // through the onboarding detour.
  const onFreePlan = !subscriptionDetails?.subscription
    || isFreePlan(subscriptionDetails, allPlans);

  // The annual plan (interval_in_days === '365'), used as the quick-user offer.
  // Falls back to any yearly-priced plan, then to the priciest paid plan.
  const annualPlan = useMemo(() => {
    const plans = allPlans || [];
    const yearly = plans.find((p) => `${p?.interval_in_days}` === '365');
    if (yearly) return yearly;
    const namedYearly = plans.find((p) => /year|annual/i.test(p?.price_description || ''));
    if (namedYearly) return namedYearly;
    // Last resort: the priciest active plan (skip the $0 free plan).
    const paidPlans = plans.filter((p) => parseFloat(p?.amount || 0) > 0);
    return paidPlans.sort((a, b) => parseFloat(b?.amount || 0) - parseFloat(a?.amount || 0))[0] || null;
  }, [allPlans]);

  // The plan this account actually holds. Built from the subscription payload —
  // the authoritative record of what they're on — and enriched from allPlans
  // when a match exists, since the feature CSV and price_description live only
  // there.
  const ownPlan = useMemo(() => {
    const sub = subscriptionDetails?.subscription;
    if (!sub) return null;
    const planId = sub.plan_id ?? sub.id;
    const matched = Array.isArray(allPlans)
      ? allPlans.find((p) => `${p?.id}` === `${planId}`)
      : null;
    return {
      ...(matched || {}),
      amount: sub.amount ?? matched?.amount ?? 0,
      product_name: sub.product_name || sub.plan || matched?.product_name,
      price_description: matched?.price_description || '',
      description: matched?.description || '',
    };
  }, [subscriptionDetails, allPlans]);

  const showingOwnPlan = !onFreePlan && !!ownPlan;
  const currentPlan = showingOwnPlan ? ownPlan : annualPlan;

  // The yearly plan is named "Premium" in the backend (interval_in_days 365,
  // "Per Year"); when it's the offer we always present it as "Annual". A real
  // plan keeps its own name.
  const planName = showingOwnPlan
    ? (subscriptionDetails?.current_plan || ownPlan?.product_name || 'Your Plan')
    : 'Annual';
  const planAmount = currentPlan?.amount != null ? `${currentPlan.amount}` : '0';
  // A $0 plan has no "was" price worth striking through.
  const showStrikePrice = parseFloat(planAmount || 0) > 0;
  const planPriceDesc = currentPlan?.price_description
    || (showingOwnPlan ? '' : 'Per Year');
  const planFeatures = useMemo(() => {
    const raw = currentPlan?.description;
    if (!raw) return DEFAULT_FEATURES;
    return raw.split(',').map((f) => f.trim()).filter(Boolean);
  }, [currentPlan]);

  // ── Renewal date (bottom-right of the plan card) ──
  // Backend renewal/expiry date is `end_date` (top-level, from getMySubscription),
  // a "YYYY-MM-DD HH:MM:SS" string. Formatted the same way as the billing
  // "Plan renews on" UI: "Month DD, YYYY" (minus one day).
  const renewalDate = useMemo(() => {
    const endDate = subscriptionDetails?.end_date || subscriptionDetails?.subscription?.end_date;
    if (!endDate) return null;
    const parsed = new Date(endDate);
    if (Number.isNaN(parsed.getTime())) return null;
    parsed.setDate(parsed.getDate() - 1);
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = parsed.toLocaleString('en-US', { month: 'long' });
    return `${month} ${day}, ${parsed.getFullYear()}`;
  }, [subscriptionDetails]);

  // Access-period phrasing for the subtext. Annual plans read "one full
  // calendar year" (the client's example); other plans use their own label.
  const accessPeriod = useMemo(() => {
    const pd = (planPriceDesc || '').toLowerCase();
    if (/year|annual/.test(pd)) return 'one full calendar year';
    if (/life/.test(pd)) return 'life';
    if (/month/.test(pd)) return 'a full month';
    if (planPriceDesc) return planPriceDesc.toLowerCase();
    return 'one full calendar year';
  }, [planPriceDesc]);

  // The offer reads as an offer. The locked case reads as a requirement —
  // promising them an access period isn't right when nothing is being sold,
  // and a monthly plan would make that sentence read wrong anyway.
  const subtitleText = showingOwnPlan
    ? 'Add a payment method to keep your Projector account active. You won’t be charged today.'
    : `Enter your payment information now. You have access to your Projector account for ${accessPeriod}.`;

  // Card form state — mirrors the in-app AddCard "stripe" flow.
  const [cardNumberStripe, setCardNumberStripe] = useState('');
  const [inputValue, setInputValue] = useState(''); // MM/YY display value
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [zipcode, setZipCode] = useState('');
  const [addingCard, setAddingCard] = useState(false);

  const digits = cardNumberStripe.replace(/\D/g, '');
  const isAmex = digits.startsWith('34') || digits.startsWith('37');

  // ── Input formatters (same behaviour as AddCard/PaymentField) ──
  const formatCardNumber = (value) => value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();

  const handleCardNumberChange = ({ target: { value } }) => {
    const onlyDigits = value.replace(/\D/g, '');
    if (onlyDigits.length <= 16) {
      setCardNumberStripe(formatCardNumber(onlyDigits));
    }
  };

  const handleExpiryDateChange = ({ target: { value } }) => {
    let newValue = value.replace(/\D/g, '');
    if (newValue.length > 2) {
      newValue = `${newValue.slice(0, 2)}/${newValue.slice(2)}`;
    }
    if (newValue.length > 5) {
      newValue = newValue.slice(0, 5);
    }
    const [month, year] = newValue.split('/');
    setExpiryMonth(month || '');
    setExpiryYear(year || '');
    setInputValue(newValue);
  };

  const handleCvcChange = (e) => {
    setCvc(e.target.value.replace(/\D/g, ''));
  };

  const handleZipCodeChange = (e) => {
    const { value } = e.target;
    const zipRegex = /^\d{0,5}(-\d{0,4})?$/;
    if (zipRegex.test(value)) {
      setZipCode(value);
    }
  };

  // ── Validation (same rules as AddCard) ──
  const cardNumberIsValid = useMemo(() => {
    if (isAmex) {
      return cardNumberStripe.length === 18 || cardNumberStripe.length === 19;
    }
    return cardNumberStripe.length === 19;
  }, [cardNumberStripe, isAmex]);

  const cvvNumberIsValid = useMemo(() => (isAmex ? cvc.length === 4 : cvc.length === 3), [cvc, isAmex]);
  const expirationDateIsValid = useMemo(() => Boolean(expiryMonth?.length && expiryYear?.length === 2), [expiryMonth, expiryYear]);

  const paymentIsValid = cardNumberIsValid && cvvNumberIsValid && expirationDateIsValid;

  // ── Stripe tokenization — identical to the in-app AddCard flow ──
  const callStripeAPI = async () => {
    const datax = new URLSearchParams();
    datax.append('card[number]', cardNumberStripe);
    datax.append('card[exp_month]', expiryMonth);
    datax.append('card[exp_year]', expiryYear);
    datax.append('card[cvc]', cvc);
    try {
      const response = await axios.post('https://api.stripe.com/v1/tokens', datax, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${STRIPE_PUBLIC_KEY}`,
        },
      });
      return response.data;
    } catch (err) {
      setAppSnackbar({ isVisible: true, type: 'error', message: err?.response?.data?.error?.message, placement: 'bottom-edge' });
      return null;
    }
  };

  const handleContinue = async () => {
    if (!paymentIsValid || addingCard) return;
    setAddingCard(true);
    try {
      const stripeToken1 = await callStripeAPI();
      const stripeToken2 = await callStripeAPI();
      const stripeTokenId1 = stripeToken1?.id;
      const stripeTokenId2 = stripeToken2?.id;
      if (!stripeTokenId1 || !stripeTokenId2) {
        throw new Error('Failed to get both Stripe tokens');
      }
      const payload = {
        card_token_alternative: stripeTokenId1,
        card_token: stripeTokenId2,
        managerCard: '0',
        zip_code: zipcode,
      };
      const response = await dataFetcher(endpoints.addPaymentMethods, payload);
      if (response?.success) {
        getSubscription?.();
        localStorage.setItem('cardEXPDialog', 'true');
        setAppSnackbar({ isVisible: true, type: 'success', message: "You're all set. No charge today", placement: 'bottom-edge' });
        // Take the user to their Projector profile / "view my Projector" page
        // (full reload so auth re-evaluates).
        window.location.href = Pathname.profile.basePath;
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: response?.message || 'Card not valid.', placement: 'bottom-edge' });
        setAddingCard(false);
      }
    } catch (err) {
      setAppSnackbar({ isVisible: true, type: 'error', message: err?.response?.data?.message || 'Card not valid.', placement: 'bottom-edge' });
      setAddingCard(false);
    }
  };

  const handleFindAnotherPlan = () => {
    // Same flow as the billing "Switch Plan" option. Pushed onto history so the
    // Switch Plan page's BACK button (navigate(-1)) returns to this page.
    navigate('/switchplan');
  };

  return (
    <div className={styles.pageContainer}>
      {/* Top bar — black app-style nav (matches the new dashboard) */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <Link to={Pathname.home}>
            <img src={logo} alt="Projector Logo" className={styles.logo} />
          </Link>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.grid}>
          {/* ── Left: current plan ── */}
          <div className={styles.leftSection}>
            <h1 className={styles.title}>Unlock Your Projector Account</h1>
            <p className={styles.subtitle}>{subtitleText}</p>

            {/* Current plan card — features come from the plans API */}
            <div className={styles.planCard}>
              <div className={styles.planCardHead}>
                <div className={styles.planNameWrap}>
                  <p className={styles.planName}>{planName}</p>
                  {planPriceDesc && <span className={styles.planDuration}>{planPriceDesc}</span>}
                </div>
                <div className={styles.planPriceWrap}>
                  <div className={styles.planPriceRow}>
                    {showStrikePrice && (
                      <span className={styles.planPriceStrike}>{`$${parseFloat(planAmount || 0).toFixed(2)}`}</span>
                    )}
                    <span className={`${styles.planPrice} ${styles.planPriceFree}`}>$0</span>
                  </div>
                  <span className={styles.planDuration}>Total owed today</span>
                </div>
              </div>
              <ul className={styles.planFeatureList}>
                {planFeatures.map((feature, index) => (
                  <li key={index} className={styles.planFeatureItem}>
                    <CheckCircleOutlineIcon className={styles.planFeatureIcon} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {renewalDate && (
                <div className={styles.planCardFoot}>
                  <span className={styles.planRenewal}>
                    {'Renews on '}
                    <strong>{renewalDate}</strong>
                  </span>
                </div>
              )}
            </div>

            <p className={styles.fineNote}>
              <strong>You won&apos;t be charged now.</strong>
              {' '}
              Keeping a card on file lets you upgrade to any other plan later, anytime.
            </p>

            {/* Explore Other Plans — hidden for quick users for now */}
            {/* <button type="button" onClick={handleFindAnotherPlan} className={styles.findPlanButton}>
              Explore Other Plans
              <ArrowForwardIcon className={styles.ctaArrow} />
            </button> */}
          </div>

          {/* ── Right: payment method card ── */}
          <div className={styles.rightSection}>
            <div className={styles.paymentCard}>
              <div className={styles.paymentCardHeader}>
                <div className={styles.paymentIconWrapper}>
                  <CreditCardIcon style={{ fontSize: '1.4rem', color: '#1172d2' }} />
                </div>
                <div>
                  <h2 className={styles.paymentTitle}>Payment Details</h2>
                  <p className={styles.paymentSubtitle}>You won&apos;t be charged now</p>
                </div>
              </div>

              {/* Clear "nothing owed today" callout — hidden for now */}
              {/* <div className={styles.owedRow}>
                <span className={styles.owedLabel}>Owed today</span>
                <span className={styles.owedAmount}>$0.00</span>
              </div> */}

              <div className={styles.formFields}>
                <div className={styles.fieldFull}>
                  <label className={styles.fieldLabel}>Card number</label>
                  <input
                    className={styles.input}
                    type="text"
                    inputMode="numeric"
                    placeholder="1234 5678 9012 3456"
                    value={cardNumberStripe}
                    onChange={handleCardNumberChange}
                  />
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.fieldHalf}>
                    <label className={styles.fieldLabel}>Expiry</label>
                    <input
                      className={styles.input}
                      type="text"
                      inputMode="numeric"
                      placeholder="MM/YY"
                      maxLength={5}
                      value={inputValue}
                      onChange={handleExpiryDateChange}
                    />
                  </div>
                  <div className={styles.fieldHalf}>
                    <label className={styles.fieldLabel}>CVC</label>
                    <input
                      className={styles.input}
                      type="text"
                      inputMode="numeric"
                      placeholder={isAmex ? '1234' : '123'}
                      maxLength={isAmex ? 4 : 3}
                      value={cvc}
                      onChange={handleCvcChange}
                    />
                  </div>
                </div>
                <div className={styles.fieldFull}>
                  <label className={styles.fieldLabel}>ZIP / Postal code</label>
                  <input
                    className={styles.input}
                    type="text"
                    inputMode="numeric"
                    placeholder="ZIP code"
                    maxLength={10}
                    value={zipcode}
                    onChange={handleZipCodeChange}
                  />
                </div>
              </div>

              <button
                type="button"
                className={styles.payButton}
                disabled={!paymentIsValid || addingCard}
                onClick={handleContinue}
              >
                {addingCard ? 'Please wait…' : 'Continue'}
              </button>

              <div className={styles.secureNote}>
                <LockOutlinedIcon style={{ fontSize: '0.95rem' }} />
                <span>Secured by Stripe &middot; Encrypted end-to-end</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
